import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Lock, Unlock, Save, X, Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BRAND } from "@/data/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImageUrl = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const APPROVAL_STATUSES = ["Pending Payment", "Confirmed", "Waitlisted"];
const PAYMENT_STATUSES = ["Pending", "Awaiting Verification", "Paid"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageEvent, setManageEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ date: "", date_label: "", venue_key: "", price_per_day: 4200 });
  const [newVenue, setNewVenue] = useState({ name: "", total_stalls: "", bulk_discount_min_stalls: 2, bulk_discount_amount: 0 });

  const [eventDrafts, setEventDrafts] = useState({});
  const [venueDrafts, setVenueDrafts] = useState({});
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [vendorSort, setVendorSort] = useState({ key: null, dir: "asc" });
  const [contactSort, setContactSort] = useState({ key: null, dir: "asc" });
  const [leadSort, setLeadSort] = useState({ key: null, dir: "asc" });
  const toggleLeadSort = (key) => setLeadSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("tmp_admin_token")}` } });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, cRes, eRes, venRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/vendors`, authHeader()),
        axios.get(`${API}/admin/contacts`, authHeader()),
        axios.get(`${API}/admin/events`, authHeader()),
        axios.get(`${API}/admin/venues`, authHeader()),
        axios.get(`${API}/admin/leads`, authHeader()),
      ]);
      setVendors(vRes.data);
      setContacts(cRes.data);
      setEvents(eRes.data);
      setVenues(venRes.data);
      setLeads(lRes.data);
      if (!newEvent.venue_key && venRes.data.length > 0) {
        setNewEvent((n) => ({ ...n, venue_key: venRes.data[0].id }));
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tmp_admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("tmp_admin_token")) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const refreshEvents = async () => {
    const eRes = await axios.get(`${API}/admin/events`, authHeader());
    setEvents(eRes.data);
    return eRes.data;
  };

  // ---------- Vendor Applications ----------
  const updateVendor = async (id, field, value) => {
    try {
      const res = await axios.patch(`${API}/admin/vendors/${id}`, { [field]: value }, authHeader());
      setVendors((vs) => vs.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
      toast.success("Updated");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const setRemarkDraft = (id, value) => setRemarkDrafts((d) => ({ ...d, [id]: value }));
  const saveRemark = async (id) => {
    if (remarkDrafts[id] === undefined) return;
    await updateVendor(id, "internal_remarks", remarkDrafts[id]);
    setRemarkDrafts((d) => { const c = { ...d }; delete c[id]; return c; });
  };
  const discardRemark = (id) => setRemarkDrafts((d) => { const c = { ...d }; delete c[id]; return c; });

  const toggleVendorSelect = (id) => setSelectedVendorIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleSelectAllVendors = () => setSelectedVendorIds((ids) => (ids.length === vendors.length ? [] : vendors.map((v) => v.id)));

  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor application permanently? This also frees up any stall it was holding.")) return;
    try {
      await axios.delete(`${API}/admin/vendors/${id}`, authHeader());
      setVendors((vs) => vs.filter((v) => v.id !== id));
      setSelectedVendorIds((ids) => ids.filter((x) => x !== id));
      toast.success("Application deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const bulkDeleteVendors = async () => {
    if (selectedVendorIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedVendorIds.length} selected application(s) permanently?`)) return;
    try {
      await axios.post(`${API}/admin/vendors/bulk-delete`, { ids: selectedVendorIds }, authHeader());
      setVendors((vs) => vs.filter((v) => !selectedVendorIds.includes(v.id)));
      setSelectedVendorIds([]);
      toast.success("Selected applications deleted");
    } catch (err) {
      toast.error("Bulk delete failed");
    }
  };

  // ---------- Contacts ----------
  const toggleContactSelect = (id) => setSelectedContactIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleSelectAllContacts = () => setSelectedContactIds((ids) => (ids.length === contacts.length ? [] : contacts.map((c) => c.id)));

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeader());
      setContacts((cs) => cs.filter((c) => c.id !== id));
      setSelectedContactIds((ids) => ids.filter((x) => x !== id));
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const bulkDeleteContacts = async () => {
    if (selectedContactIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedContactIds.length} selected enquiry(ies) permanently?`)) return;
    try {
      await axios.post(`${API}/admin/contacts/bulk-delete`, { ids: selectedContactIds }, authHeader());
      setContacts((cs) => cs.filter((c) => !selectedContactIds.includes(c.id)));
      setSelectedContactIds([]);
      toast.success("Selected enquiries deleted");
    } catch (err) {
      toast.error("Bulk delete failed");
    }
  };

  // ---------- Entry Leads ----------
  const toggleLeadSelect = (id) => setSelectedLeadIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleSelectAllLeads = () => setSelectedLeadIds((ids) => (ids.length === leads.length ? [] : leads.map((l) => l.id)));

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, authHeader());
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setSelectedLeadIds((ids) => ids.filter((x) => x !== id));
      toast.success("Entry deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const bulkDeleteLeads = async () => {
    if (selectedLeadIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedLeadIds.length} selected entry(ies) permanently?`)) return;
    try {
      await axios.post(`${API}/admin/leads/bulk-delete`, { ids: selectedLeadIds }, authHeader());
      setLeads((ls) => ls.filter((l) => !selectedLeadIds.includes(l.id)));
      setSelectedLeadIds([]);
      toast.success("Selected entries deleted");
    } catch (err) {
      toast.error("Bulk delete failed");
    }
  };

  const exportToExcel = async (type) => {
    try {
      const res = await axios.get(`${API}/admin/${type}/export`, { ...authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${type}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  // ---------- Events ----------
  const setEventDraftField = (id, field, value) => setEventDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  const discardEventDraft = (id) => setEventDrafts((d) => { const c = { ...d }; delete c[id]; return c; });

  const createEvent = async () => {
    if (!newEvent.date || !newEvent.date_label || !newEvent.venue_key) {
      toast.error("Fill in date, label and venue.");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/events`, { ...newEvent, price_per_day: Number(newEvent.price_per_day) || 0 }, authHeader());
      await refreshEvents();
      setNewEvent({ date: "", date_label: "", venue_key: venues[0]?.id || "", price_per_day: 4200 });
      toast.success("Event added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add event");
    }
  };

  const saveEventDraft = async (id) => {
    const draft = eventDrafts[id];
    if (!draft || Object.keys(draft).length === 0) return;
    try {
      const payload = { ...draft };
      if (payload.price_per_day !== undefined) payload.price_per_day = Number(payload.price_per_day);
      await axios.patch(`${API}/admin/events/${id}`, payload, authHeader());
      await refreshEvents();
      discardEventDraft(id);
      toast.success("Event saved");
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const updateEventActive = async (id, isActive) => {
    try {
      await axios.patch(`${API}/admin/events/${id}`, { is_active: isActive }, authHeader());
      setEvents((evs) => evs.map((e) => (e.id === id ? { ...e, is_active: isActive } : e)));
      toast.success(isActive ? "Event activated" : "Event deactivated");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, authHeader());
      setEvents((evs) => evs.filter((e) => e.id !== id));
      toast.success("Event removed");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const toggleBlockStall = async (eventId, stallNumber, blocked) => {
    try {
      await axios.post(`${API}/admin/events/${eventId}/block-stall`, { stall_number: stallNumber, blocked }, authHeader());
      const updated = await refreshEvents();
      setManageEvent(updated.find((e) => e.id === eventId) || null);
    } catch (err) {
      toast.error("Could not update stall");
    }
  };

  const unblockAllStalls = async (eventId) => {
    if (!window.confirm("Unblock ALL manually-blocked stalls for this event?")) return;
    try {
      await axios.patch(`${API}/admin/events/${eventId}`, { blocked_stalls: [] }, authHeader());
      const updated = await refreshEvents();
      setManageEvent(updated.find((e) => e.id === eventId) || null);
      toast.success("All stalls unblocked");
    } catch (err) {
      toast.error("Could not unblock stalls");
    }
  };

  const releaseStallHold = async (eventId, applicationId) => {
    if (!window.confirm("This deletes the pending/unpaid application holding this stall, freeing it up immediately. Continue?")) return;
    try {
      await axios.delete(`${API}/admin/vendors/${applicationId}`, authHeader());
      const [updatedEvents, vRes] = await Promise.all([refreshEvents(), axios.get(`${API}/admin/vendors`, authHeader())]);
      setManageEvent(updatedEvents.find((e) => e.id === eventId) || null);
      setVendors(vRes.data);
      toast.success("Stall released");
    } catch (err) {
      toast.error("Could not release stall");
    }
  };

  // ---------- Venues ----------
  const setVenueDraftField = (id, field, value) => setVenueDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
  const discardVenueDraft = (id) => setVenueDrafts((d) => { const c = { ...d }; delete c[id]; return c; });

  const createVenue = async () => {
    if (!newVenue.name || !newVenue.total_stalls) {
      toast.error("Fill in venue name and number of stalls.");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/venues`, {
        ...newVenue,
        total_stalls: Number(newVenue.total_stalls) || 0,
        bulk_discount_min_stalls: Number(newVenue.bulk_discount_min_stalls) || 0,
        bulk_discount_amount: Number(newVenue.bulk_discount_amount) || 0,
      }, authHeader());
      setVenues((vs) => [...vs, res.data]);
      setNewVenue({ name: "", total_stalls: "", bulk_discount_min_stalls: 2, bulk_discount_amount: 0 });
      toast.success("Venue added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add venue");
    }
  };

  const saveVenueDraft = async (id) => {
    const draft = venueDrafts[id];
    if (!draft || Object.keys(draft).length === 0) return;
    try {
      const payload = { ...draft };
      ["total_stalls", "bulk_discount_min_stalls", "bulk_discount_amount"].forEach((f) => {
        if (payload[f] !== undefined) payload[f] = Number(payload[f]);
      });
      const res = await axios.patch(`${API}/admin/venues/${id}`, payload, authHeader());
      setVenues((vs) => vs.map((v) => (v.id === id ? res.data : v)));
      discardVenueDraft(id);
      toast.success("Venue saved");
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const uploadVenueImage = async (id, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/venues/${id}/image`, formData, {
        headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      setVenues((vs) => vs.map((v) => (v.id === id ? res.data : v)));
      toast.success("Picture updated");
    } catch (err) {
      toast.error("Could not upload picture");
    }
  };

  const deleteVenue = async (id) => {
    if (!window.confirm("Delete this venue permanently?")) return;
    try {
      await axios.delete(`${API}/admin/venues/${id}`, authHeader());
      setVenues((vs) => vs.filter((v) => v.id !== id));
      toast.success("Venue removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("tmp_admin_token");
    navigate("/admin");
  };

  // ---------- Sorting ----------
  const sortRows = (rows, { key, dir }) => {
    if (!key) return rows;
    const sorted = [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av ?? "").toLowerCase().localeCompare(String(bv ?? "").toLowerCase());
    });
    return dir === "asc" ? sorted : sorted.reverse();
  };
  const makeToggleSort = (setter) => (key) => setter((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const toggleVendorSort = makeToggleSort(setVendorSort);
  const toggleContactSort = makeToggleSort(setContactSort);

  const SortHead = ({ label, sortKey, config, onSort }) => (
    <TableHead>
      <button onClick={() => onSort(sortKey)} data-testid={`sort-${sortKey}`} className="flex items-center gap-1 hover:text-[#D81B60]">
        {label}
        {config.key === sortKey ? (config.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
      </button>
    </TableHead>
  );

  // ---------- Month-wise sales report ----------
  const monthLabel = (key) => {
    const [y, m] = key.split("-");
    return new Date(Number(y), Number(m) - 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  };
  const salesByMonth = useMemo(() => {
    const map = {};
    vendors.forEach((v) => {
      if (!v.created_at) return;
      const d = new Date(v.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!map[key]) map[key] = { month: key, bookings: 0, paid: 0, pending: 0 };
      map[key].bookings += 1;
      const amt = v.amount_due || 0;
      if (v.payment_status === "Paid") map[key].paid += amt;
      else map[key].pending += amt;
    });
    return Object.values(map).sort((a, b) => b.month.localeCompare(a.month));
  }, [vendors]);
  const salesTotals = salesByMonth.reduce(
    (t, r) => ({ bookings: t.bookings + r.bookings, paid: t.paid + r.paid, pending: t.pending + r.pending }),
    { bookings: 0, paid: 0, pending: 0 }
  );

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400 font-mono text-sm">Loading dashboard...</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="admin-dashboard">
      <div className="border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between">
        <span className="font-extrabold text-lg">{BRAND.name} — Admin</span>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500">
          <LogOut size={15} /> Logout
        </button>
      </div>

      <div className="p-4 sm:p-8 max-w-7xl mx-auto">
        <Tabs defaultValue="events">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="events" data-testid="tab-events">Events ({events.length})</TabsTrigger>
            <TabsTrigger value="venues" data-testid="tab-venues">Venues ({venues.length})</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors">Vendor Applications ({vendors.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Enquiries ({contacts.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Entry Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales">Sales Report</TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <div className="rounded-2xl border border-gray-200 bg-white mt-4 p-4 sm:p-6">
              <p className="font-bold text-sm mb-3">Add New Event</p>
              <div className="grid sm:grid-cols-5 gap-3 mb-2">
                <Input
                  type="date"
                  data-testid="new-event-date-input"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent((n) => ({ ...n, date: e.target.value }))}
                  className="h-9 text-xs"
                />
                <Input
                  placeholder="Label e.g. Sep 6, 2026"
                  data-testid="new-event-label-input"
                  value={newEvent.date_label}
                  onChange={(e) => setNewEvent((n) => ({ ...n, date_label: e.target.value }))}
                  className="h-9 text-xs"
                />
                <Select value={newEvent.venue_key} onValueChange={(val) => setNewEvent((n) => ({ ...n, venue_key: val }))}>
                  <SelectTrigger data-testid="new-event-venue-select" className="h-9 text-xs">
                    <SelectValue placeholder="Venue" />
                  </SelectTrigger>
                  <SelectContent>
                    {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.total_stalls} stalls)</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Price/day"
                  data-testid="new-event-price-input"
                  value={newEvent.price_per_day}
                  onChange={(e) => setNewEvent((n) => ({ ...n, price_per_day: e.target.value }))}
                  className="h-9 text-xs"
                />
                <button onClick={createEvent} data-testid="new-event-submit-button" className="flex items-center justify-center gap-1.5 rounded-lg bg-[#D81B60] text-white text-xs font-semibold hover:bg-[#AD144B] transition-colors duration-300">
                  <Plus size={14} /> Add Event
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white mt-4">
              <Table data-testid="events-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Price/Day</TableHead>
                    <TableHead>Blocked Stalls</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e) => {
                    const draft = eventDrafts[e.id];
                    const hasDraft = draft && Object.keys(draft).length > 0;
                    return (
                      <TableRow key={e.id} data-testid={`event-row-${e.date}`}>
                        <TableCell>
                          <Input
                            type="date"
                            value={draft?.date ?? e.date}
                            data-testid={`event-date-input-${e.date}`}
                            onChange={(ev) => setEventDraftField(e.id, "date", ev.target.value)}
                            className="w-36 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={draft?.date_label ?? e.date_label}
                            data-testid={`event-label-input-${e.date}`}
                            onChange={(ev) => setEventDraftField(e.id, "date_label", ev.target.value)}
                            className="w-32 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell className="text-xs">{e.venue_name}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={draft?.price_per_day ?? e.price_per_day}
                            data-testid={`event-price-input-${e.date}`}
                            onChange={(ev) => setEventDraftField(e.id, "price_per_day", ev.target.value)}
                            className="w-24 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <button onClick={() => setManageEvent(e)} data-testid={`manage-stalls-button-${e.date}`} className="text-xs underline text-[#D81B60]">
                            {(e.blocked_stalls || []).length} blocked — Manage
                          </button>
                        </TableCell>
                        <TableCell>
                          <Switch checked={e.is_active} onCheckedChange={(val) => updateEventActive(e.id, val)} data-testid={`event-active-switch-${e.date}`} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {hasDraft && (
                              <>
                                <button onClick={() => saveEventDraft(e.id)} data-testid={`save-event-button-${e.date}`} className="text-green-600 hover:text-green-700" title="Save changes">
                                  <Save size={16} />
                                </button>
                                <button onClick={() => discardEventDraft(e.id)} data-testid={`discard-event-button-${e.date}`} className="text-gray-400 hover:text-gray-600" title="Discard changes">
                                  <X size={16} />
                                </button>
                              </>
                            )}
                            <button onClick={() => deleteEvent(e.id)} data-testid={`delete-event-button-${e.date}`} className="text-gray-400 hover:text-red-500">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && events.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">No events yet. Add one above.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="venues">
            <div className="rounded-2xl border border-gray-200 bg-white mt-4 p-4 sm:p-6">
              <p className="font-bold text-sm mb-3">Add New Venue</p>
              <div className="grid sm:grid-cols-5 gap-3">
                <Input
                  placeholder="Venue name"
                  data-testid="new-venue-name-input"
                  value={newVenue.name}
                  onChange={(e) => setNewVenue((n) => ({ ...n, name: e.target.value }))}
                  className="h-9 text-xs"
                />
                <Input
                  type="number"
                  placeholder="Total stalls"
                  data-testid="new-venue-total-stalls-input"
                  value={newVenue.total_stalls}
                  onChange={(e) => setNewVenue((n) => ({ ...n, total_stalls: e.target.value }))}
                  className="h-9 text-xs"
                />
                <Input
                  type="number"
                  placeholder="Discount if min. stalls"
                  data-testid="new-venue-discount-min-input"
                  value={newVenue.bulk_discount_min_stalls}
                  onChange={(e) => setNewVenue((n) => ({ ...n, bulk_discount_min_stalls: e.target.value }))}
                  className="h-9 text-xs"
                />
                <Input
                  type="number"
                  placeholder="Discount amount ₹"
                  data-testid="new-venue-discount-amount-input"
                  value={newVenue.bulk_discount_amount}
                  onChange={(e) => setNewVenue((n) => ({ ...n, bulk_discount_amount: e.target.value }))}
                  className="h-9 text-xs"
                />
                <button onClick={createVenue} data-testid="new-venue-submit-button" className="flex items-center justify-center gap-1.5 rounded-lg bg-[#D81B60] text-white text-xs font-semibold hover:bg-[#AD144B] transition-colors duration-300">
                  <Plus size={14} /> Add Venue
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">E.g. "Discount if min. stalls" = 2 and "Discount amount" = ₹300 means: book 2+ stalls at this venue, get a flat ₹300 off the total. Set min. stalls to 0 to disable.</p>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white mt-4">
              <Table data-testid="venues-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Picture</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Total Stalls</TableHead>
                    <TableHead>Discount if min. stalls</TableHead>
                    <TableHead>Discount amount</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {venues.map((v) => {
                    const draft = venueDrafts[v.id];
                    const hasDraft = draft && Object.keys(draft).length > 0;
                    return (
                      <TableRow key={v.id} data-testid={`venue-row-${v.id}`}>
                        <TableCell>
                          {v.image_url ? (
                            <img src={resolveImageUrl(v.image_url)} alt={v.name} className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">No image</div>
                          )}
                          <label className="text-[10px] text-[#D81B60] underline cursor-pointer mt-1 block">
                            Change Picture
                            <input type="file" accept="image/*" className="hidden" data-testid={`venue-image-input-${v.id}`} onChange={(e) => e.target.files[0] && uploadVenueImage(v.id, e.target.files[0])} />
                          </label>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={draft?.name ?? v.name}
                            data-testid={`venue-name-input-${v.id}`}
                            onChange={(e) => setVenueDraftField(v.id, "name", e.target.value)}
                            className="w-40 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={draft?.total_stalls ?? v.total_stalls}
                            data-testid={`venue-total-stalls-input-${v.id}`}
                            onChange={(e) => setVenueDraftField(v.id, "total_stalls", e.target.value)}
                            className="w-20 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={draft?.bulk_discount_min_stalls ?? v.bulk_discount_min_stalls}
                            data-testid={`venue-discount-min-input-${v.id}`}
                            onChange={(e) => setVenueDraftField(v.id, "bulk_discount_min_stalls", e.target.value)}
                            className="w-20 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={draft?.bulk_discount_amount ?? v.bulk_discount_amount}
                            data-testid={`venue-discount-amount-input-${v.id}`}
                            onChange={(e) => setVenueDraftField(v.id, "bulk_discount_amount", e.target.value)}
                            className="w-24 h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {hasDraft && (
                              <>
                                <button onClick={() => saveVenueDraft(v.id)} data-testid={`save-venue-button-${v.id}`} className="text-green-600 hover:text-green-700" title="Save changes">
                                  <Save size={16} />
                                </button>
                                <button onClick={() => discardVenueDraft(v.id)} data-testid={`discard-venue-button-${v.id}`} className="text-gray-400 hover:text-gray-600" title="Discard changes">
                                  <X size={16} />
                                </button>
                              </>
                            )}
                            <button onClick={() => deleteVenue(v.id)} data-testid={`delete-venue-button-${v.id}`} className="text-gray-400 hover:text-red-500">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && venues.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No venues yet. Add one above.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="vendors">
            <div className="flex items-center justify-between mt-4 mb-3">
              <button
                onClick={bulkDeleteVendors}
                disabled={selectedVendorIds.length === 0}
                data-testid="bulk-delete-vendors-button"
                className="flex items-center gap-1.5 text-sm rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:border-red-400 hover:text-red-500"
              >
                <Trash2 size={14} /> Delete Selected ({selectedVendorIds.length})
              </button>
              <button onClick={() => exportToExcel("vendors")} data-testid="export-vendors-button" className="flex items-center gap-1.5 text-sm rounded-lg bg-[#1A1A1A] text-white px-3 py-1.5 hover:bg-black">
                <Download size={14} /> Export to Excel
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
              <Table data-testid="vendors-table">
                <TableHeader>
                  <TableRow>
                    <TableHead><Checkbox checked={vendors.length > 0 && selectedVendorIds.length === vendors.length} onCheckedChange={toggleSelectAllVendors} data-testid="select-all-vendors-checkbox" /></TableHead>
                    <SortHead label="Reference" sortKey="reference_id" config={vendorSort} onSort={toggleVendorSort} />
                    <SortHead label="Business" sortKey="business_name" config={vendorSort} onSort={toggleVendorSort} />
                    <SortHead label="Category" sortKey="category_of_product" config={vendorSort} onSort={toggleVendorSort} />
                    <SortHead label="Contact" sortKey="contact_person_name" config={vendorSort} onSort={toggleVendorSort} />
                    <TableHead>Stalls</TableHead>
                    <SortHead label="Amount" sortKey="amount_due" config={vendorSort} onSort={toggleVendorSort} />
                    <SortHead label="Payment" sortKey="payment_status" config={vendorSort} onSort={toggleVendorSort} />
                    <SortHead label="Approval" sortKey="approval_status" config={vendorSort} onSort={toggleVendorSort} />
                    <TableHead>Remarks</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortRows(vendors, vendorSort).map((v) => (
                    <TableRow key={v.id} data-testid={`vendor-row-${v.reference_id}`}>
                      <TableCell><Checkbox checked={selectedVendorIds.includes(v.id)} onCheckedChange={() => toggleVendorSelect(v.id)} data-testid={`select-vendor-${v.reference_id}`} /></TableCell>
                      <TableCell className="text-xs font-mono">{v.reference_id}</TableCell>
                      <TableCell className="text-xs">{v.business_name}</TableCell>
                      <TableCell className="text-xs" data-testid={`vendor-category-${v.reference_id}`}>{v.category_of_product || "—"}</TableCell>
                      <TableCell className="text-xs">{v.contact_person_name}<br />{v.phone_number}</TableCell>
                      <TableCell className="text-xs">{(v.stall_selections || []).map((s) => `${s.date} #${s.stall_number}`).join(", ")}</TableCell>
                      <TableCell className="text-xs">₹{v.amount_due?.toLocaleString("en-IN")}</TableCell>
                      <TableCell>
                        <Select value={v.payment_status} onValueChange={(val) => updateVendor(v.id, "payment_status", val)}>
                          <SelectTrigger data-testid={`payment-status-select-${v.reference_id}`} className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={v.approval_status} onValueChange={(val) => updateVendor(v.id, "approval_status", val)}>
                          <SelectTrigger data-testid={`approval-status-select-${v.reference_id}`} className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>{APPROVAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={remarkDrafts[v.id] ?? v.internal_remarks ?? ""}
                            data-testid={`remarks-input-${v.reference_id}`}
                            onChange={(e) => setRemarkDraft(v.id, e.target.value)}
                            className="w-32 h-8 text-xs"
                          />
                          {remarkDrafts[v.id] !== undefined && (
                            <>
                              <button onClick={() => saveRemark(v.id)} data-testid={`save-remarks-button-${v.reference_id}`} className="text-green-600 hover:text-green-700"><Save size={14} /></button>
                              <button onClick={() => discardRemark(v.id)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <button onClick={() => deleteVendor(v.id)} data-testid={`delete-vendor-button-${v.reference_id}`} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {vendors.length === 0 && (
                    <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-8">No vendor applications yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="flex items-center justify-between mt-4 mb-3">
              <button
                onClick={bulkDeleteContacts}
                disabled={selectedContactIds.length === 0}
                data-testid="bulk-delete-contacts-button"
                className="flex items-center gap-1.5 text-sm rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:border-red-400 hover:text-red-500"
              >
                <Trash2 size={14} /> Delete Selected ({selectedContactIds.length})
              </button>
              <button onClick={() => exportToExcel("contacts")} data-testid="export-contacts-button" className="flex items-center gap-1.5 text-sm rounded-lg bg-[#1A1A1A] text-white px-3 py-1.5 hover:bg-black">
                <Download size={14} /> Export to Excel
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
              <Table data-testid="contacts-table">
                <TableHeader>
                  <TableRow>
                    <TableHead><Checkbox checked={contacts.length > 0 && selectedContactIds.length === contacts.length} onCheckedChange={toggleSelectAllContacts} data-testid="select-all-contacts-checkbox" /></TableHead>
                    <SortHead label="Name" sortKey="name" config={contactSort} onSort={toggleContactSort} />
                    <SortHead label="Email" sortKey="email" config={contactSort} onSort={toggleContactSort} />
                    <SortHead label="Phone" sortKey="phone" config={contactSort} onSort={toggleContactSort} />
                    <SortHead label="Type" sortKey="enquiry_type" config={contactSort} onSort={toggleContactSort} />
                    <SortHead label="Message" sortKey="message" config={contactSort} onSort={toggleContactSort} />
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortRows(contacts, contactSort).map((c) => (
                    <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                      <TableCell><Checkbox checked={selectedContactIds.includes(c.id)} onCheckedChange={() => toggleContactSelect(c.id)} data-testid={`select-contact-${c.id}`} /></TableCell>
                      <TableCell className="text-xs">{c.name}</TableCell>
                      <TableCell className="text-xs">{c.email}</TableCell>
                      <TableCell className="text-xs">{c.phone}</TableCell>
                      <TableCell className="text-xs">{c.enquiry_type}</TableCell>
                      <TableCell className="text-xs max-w-xs truncate">{c.message}</TableCell>
                      <TableCell>
                        <button onClick={() => deleteContact(c.id)} data-testid={`delete-contact-button-${c.id}`} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {contacts.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">No enquiries yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <div className="flex items-center justify-between mt-4 mb-3">
              <button
                onClick={bulkDeleteLeads}
                disabled={selectedLeadIds.length === 0}
                data-testid="bulk-delete-leads-button"
                className="flex items-center gap-1.5 text-sm rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:border-red-400 hover:text-red-500"
              >
                <Trash2 size={14} /> Delete Selected ({selectedLeadIds.length})
              </button>
              <button onClick={() => exportToExcel("leads")} data-testid="export-leads-button" className="flex items-center gap-1.5 text-sm rounded-lg bg-[#1A1A1A] text-white px-3 py-1.5 hover:bg-black">
                <Download size={14} /> Export to Excel
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead><Checkbox checked={leads.length > 0 && selectedLeadIds.length === leads.length} onCheckedChange={toggleSelectAllLeads} data-testid="select-all-leads-checkbox" /></TableHead>
                    <SortHead label="Name" sortKey="name" config={leadSort} onSort={toggleLeadSort} />
                    <SortHead label="Mobile" sortKey="mobile" config={leadSort} onSort={toggleLeadSort} />
                    <SortHead label="Email" sortKey="email" config={leadSort} onSort={toggleLeadSort} />
                    <SortHead label="Location" sortKey="location" config={leadSort} onSort={toggleLeadSort} />
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortRows(leads, leadSort).map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell><Checkbox checked={selectedLeadIds.includes(l.id)} onCheckedChange={() => toggleLeadSelect(l.id)} data-testid={`select-lead-${l.id}`} /></TableCell>
                      <TableCell className="text-xs">{l.name}</TableCell>
                      <TableCell className="text-xs">{l.mobile}</TableCell>
                      <TableCell className="text-xs">{l.email || "—"}</TableCell>
                      <TableCell className="text-xs">{l.location}</TableCell>
                      <TableCell>
                        <button onClick={() => deleteLead(l.id)} data-testid={`delete-lead-button-${l.id}`} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {leads.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No entries yet. Share the /entry link to start collecting.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="sales">
            <div className="rounded-2xl border border-gray-200 bg-white mt-4 p-4 sm:p-6">
              <p className="font-bold text-sm mb-1">Month-wise Sale Report</p>
              <p className="text-[11px] text-gray-400 mb-4">Grouped by booking date. Paid = revenue received; Pending = amount from unpaid / awaiting-verification bookings.</p>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="rounded-xl bg-[#FAFAF8] border border-gray-200 p-3">
                  <p className="text-[11px] text-gray-400">Total Bookings</p>
                  <p className="font-extrabold text-lg" data-testid="sales-total-bookings">{salesTotals.bookings}</p>
                </div>
                <div className="rounded-xl bg-green-50 border border-green-200 p-3">
                  <p className="text-[11px] text-green-600">Paid Revenue</p>
                  <p className="font-extrabold text-lg text-green-700" data-testid="sales-total-paid">₹{salesTotals.paid.toLocaleString("en-IN")}</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <p className="text-[11px] text-amber-600">Pending</p>
                  <p className="font-extrabold text-lg text-amber-700" data-testid="sales-total-pending">₹{salesTotals.pending.toLocaleString("en-IN")}</p>
                </div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <Table data-testid="sales-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead>Bookings</TableHead>
                      <TableHead>Paid Revenue</TableHead>
                      <TableHead>Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesByMonth.map((r) => (
                      <TableRow key={r.month} data-testid={`sales-row-${r.month}`}>
                        <TableCell className="text-xs font-semibold">{monthLabel(r.month)}</TableCell>
                        <TableCell className="text-xs">{r.bookings}</TableCell>
                        <TableCell className="text-xs text-green-700">₹{r.paid.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-xs text-amber-700">₹{r.pending.toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                    {salesByMonth.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-8">No bookings yet.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!manageEvent} onOpenChange={(open) => !open && setManageEvent(null)}>
        <DialogContent data-testid="manage-stalls-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Stalls — {manageEvent?.date_label} ({manageEvent?.venue_name})</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-1">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-200 inline-block" /> Blocked by you (click to unblock)</span>
            {" · "}
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-200 inline-block" /> Unpaid hold (click to release)</span>
            {" · "}
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-green-200 inline-block" /> Paid (locked)</span>
          </p>
          {(manageEvent?.blocked_stalls || []).length > 0 && (
            <button onClick={() => unblockAllStalls(manageEvent.id)} data-testid="unblock-all-stalls-button" className="text-xs text-[#D81B60] underline mb-2 self-start">
              Unblock all {manageEvent.blocked_stalls.length} manually-blocked stalls
            </button>
          )}
          <div className="grid grid-cols-6 gap-2 max-h-80 overflow-y-auto">
            {manageEvent && Array.from({ length: manageEvent.total_stalls || 0 }, (_, i) => i + 1).map((num) => {
              const isBlocked = (manageEvent.blocked_stalls || []).includes(num);
              const booking = (manageEvent.booked_stalls || []).find((b) => b.stall_number === num);
              const isPaid = booking && booking.payment_status === "Paid";
              let classes = "bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#D81B60]";
              let title = `Stall ${num} — Available`;
              if (isBlocked) {
                classes = "bg-red-100 text-red-600 border border-red-300";
                title = `Stall ${num} — Blocked by you. Click to unblock.`;
              } else if (isPaid) {
                classes = "bg-green-100 text-green-700 border border-green-300 cursor-default";
                title = `Stall ${num} — Paid by ${booking.business_name} (${booking.reference_id})`;
              } else if (booking) {
                classes = "bg-amber-100 text-amber-700 border border-amber-300";
                title = `Stall ${num} — Unpaid hold by ${booking.business_name} (${booking.payment_status}). Click to release.`;
              }
              return (
                <button
                  key={num}
                  onClick={() => {
                    if (isPaid) return;
                    if (booking) releaseStallHold(manageEvent.id, booking.application_id);
                    else toggleBlockStall(manageEvent.id, num, !isBlocked);
                  }}
                  data-testid={`admin-block-stall-${num}`}
                  title={title}
                  className={`rounded-lg h-10 text-xs font-bold flex items-center justify-center gap-1 transition-colors duration-200 ${classes}`}
                >
                  {isBlocked ? <Lock size={11} /> : !booking ? <Unlock size={11} /> : null} {num}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
